/**
 * Проведение игры со стороны участника (после ввода ника).
 *
 * Подключается к /ws?pin=<pin> с role=player и авто-join с ником. Экраны:
 * - подключение/лобби: «Ожидаем начала игры…»;
 * - question: варианты-кнопки + серверный обратный отсчёт; клик → answer;
 * - reveal: подсветка верного/неверного + живой рейтинг (LiveLeaderboard);
 * - finished: эффектный финал (FinishScreen) с местом игрока.
 *
 * И7-полировка: живой лидерборд, звук (клик ответа / верно-неверно / финал),
 * кнопка mute (SoundToggle). Ответ идемпотентен: локально блокируем повтор, а
 * already_answered с сервера игнорируем молча (протокол §6).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { savePlayer } from '../activeSession';
import { clearPlayerToken, loadPlayerToken, savePlayerToken } from '../playerToken';
import { suggestNicks } from '../nickSuggestions';
import { LobbyRoster } from '../components/LobbyRoster';
import { QuestionView } from '../components/QuestionView';
import { LiveLeaderboard } from '../components/LiveLeaderboard';
import { FinishScreen } from '../components/FinishScreen';
import { SoundToggle } from '../components/SoundToggle';
import { StatusPill } from '../components/ui';
import { useQuizSocket } from '../hooks';
import {
  ErrorCode,
  makeAnswerPayload,
  type Phase,
  type PlacementPayload,
  type PlayerAnswer,
  type QuestionPayload,
  type RevealPayload,
  type RosterEntry,
  type RunDeadPayload,
  type ScoreboardEntry,
  type ServerMessage,
} from '../protocol';
import { wsErrorKey } from '../util';
import { ensureLanguages } from '../languages';
import { playClick, playFinish, playCorrect, playReveal, playWrong, unlockAudio } from '../sound';

export interface PlayerGameProps {
  pin: string;
  nick: string;
  onExit: () => void;
  /**
   * Фаза 13 (Эпик A2b): сервер отклонил join кодом `nick_taken`. Экран
   * «ник занят» зовёт колбэк: с кандидатом (тап по подсказке — ник
   * подставится в форму) или null (кнопка «Другой ник»). Авто-join по
   * подсказке НЕ делается — следующий join инициирует сам игрок.
   */
  onNickRejected?: (suggestion: string | null) => void;
}

/** Место игрока в последнем рейтинге (1-based). */
function computePlacement(entries: ScoreboardEntry[], nick: string): PlacementPayload | null {
  const sorted = [...entries].sort((a, b) => b.score - a.score || a.nick.localeCompare(b.nick));
  const index = sorted.findIndex((e) => e.nick === nick);
  if (index === -1) return null;
  return { rank: index + 1, score: sorted[index].score };
}

/** Определяет, верен ли ответ игрока по reveal (для звука/индикации). Пока — только choice
 *  (где это тривиально); для прочих типов даём нейтральный reveal-звук. */
function isMyAnswerCorrect(reveal: RevealPayload, myAnswer: PlayerAnswer | null): boolean | null {
  if (!myAnswer) return null;
  const kind = reveal.kind ?? 'choice';
  if (kind === 'choice' && myAnswer.kind === 'choice' && reveal.correctOption !== undefined) {
    return myAnswer.option === reveal.correctOption;
  }
  return null; // неизвестно (open/match/order) — нейтрально.
}

export function PlayerGame({ pin, nick, onExit, onNickRejected }: PlayerGameProps): JSX.Element {
  const { t } = useTranslation();
  const [joined, setJoined] = useState(false);
  const [phase, setPhase] = useState<Phase>('lobby');
  const [question, setQuestion] = useState<QuestionPayload | null>(null);
  // И4 (Фаза 10): режим «счёт после каждого вопроса» — из state-события
  // (сервер шлёт флаг всем, включая reconnect/опоздавших).
  const [scorePause, setScorePause] = useState(false);
  const [myAnswer, setMyAnswer] = useState<PlayerAnswer | null>(null);
  const [reveal, setReveal] = useState<RevealPayload | null>(null);
  const [scoreboard, setScoreboard] = useState<ScoreboardEntry[]>([]);
  // Фаза 11 (И6прот): серверный placement при N>K + total топ-K-рейтинга.
  const [placement, setPlacement] = useState<PlacementPayload | null>(null);
  const [boardTotal, setBoardTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  // Фаза 13 (Эпик A2b): сервер отклонил nick при strictNick — экран «ник занят»
  // с подсказками вместо Fatal-баннера; выход — к форме ввода ника.
  const [nickTaken, setNickTaken] = useState(false);
  // Фаза 13 (Эпик B1): этот сокет замещён новым подключением слота (другая
  // вкладка/устройство). Тихое завершение UI-сессии: WS-слой уже закрыл сокет
  // без реконнекта; токен в localStorage СОХРАНЯЕТСЯ — владелец слота тот же.
  const [superseded, setSuperseded] = useState(false);
  // Фаза 15 (Эпик B3): ведущий удалил участника (error kicked + закрытие WS).
  // Конечный экран без кнопки переподключения: ws-слой не реконнектится по
  // этому коду, повторный join слота (токен/ник) снова отклоняется.
  const [kicked, setKicked] = useState(false);
  // И5: доска участников лобби + свой playerID/ник (для self-rename и RG13-синхронизации).
  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [myPlayerId, setMyPlayerId] = useState<number | null>(null);
  const [currentNick, setCurrentNick] = useState(nick);
  // 0.14.3 (S4): персональный dead-пуш run_dead — сервер зафиксировал гибель
  // в run-вопросе (даже если локальный детект не сработал из-за фазового
  // сдвига). Прокидывается в RunView (мгновенный взрыв + наблюдатель);
  // сбрасывается на каждом новом вопросе.
  const [runDead, setRunDead] = useState<RunDeadPayload | null>(null);

  function handleMessage(message: ServerMessage): void {
    switch (message.type) {
      case 'joined':
        setJoined(true);
        setMyPlayerId(message.payload.you.id);
        // Фаза 13 (Эпик A2): сервер выпустил токен (новый слот / legacy-resume)
        // → сохраняем на устройстве; при resume по токену поле не приходит —
        // хранимое значение не трогаем.
        if (message.payload.playerToken) {
          savePlayerToken(pin, message.payload.playerToken);
        }
        break;
      case 'roster': {
        // И5: обновляем доску; если сменился наш ник (свой rename или хост-rename),
        // актуализируем currentNick и sessionStorage — иначе session-resume по нику
        // сломался бы после хост-rename (риск RG13).
        const entries = message.payload.entries;
        setRoster(entries);
        const self =
          myPlayerId !== null ? entries.find((e) => e.playerId === myPlayerId) : undefined;
        if (self && self.nick !== currentNick) {
          setCurrentNick(self.nick);
          savePlayer(pin, self.nick);
        }
        break;
      }
      case 'state':
        setPhase(message.payload.phase);
        setScorePause(message.payload.scorePause ?? false);
        // Фаза 16 (B2): предзагрузка грамматик подсветки по langs квиза —
        // событийно при state-снапшоте (join/reconnect), без блокировки
        // рендера: грузится за лобби, к первому вопросу чанки готовы.
        // Идемпотентно; квиз без кода (langs отсутствует) чанки не грузит.
        if (message.payload.langs?.length) {
          void ensureLanguages(message.payload.langs);
        }
        break;
      case 'question':
        setQuestion(message.payload);
        setMyAnswer(null);
        setReveal(null);
        // 0.14.3 (S4): dead-пуш относится к вопросу — новый вопрос обнуляет.
        setRunDead(null);
        // Фаза 11 (И6прот): placement устаревает вместе с экраном вопроса.
        setPlacement(null);
        setError(null);
        break;
      case 'run_dead':
        // 0.14.3 (S4): персональное уведомление о гибели в активном run-вопросе.
        // Сервер шлёт только погибшему онлайн; идемпотентно локальному детекту
        // (RunView first-wins) и kindData.dead при reconnect.
        setRunDead(message.payload);
        break;
      case 'reveal':
        setReveal(message.payload);
        // Звук раскрытия: для choice — верно/неверно; для прочих — нейтральный.
        {
          const verdict = isMyAnswerCorrect(message.payload, myAnswer);
          if (verdict === true) playCorrect();
          else if (verdict === false) playWrong();
          else playReveal();
        }
        break;
      case 'scoreboard':
        setScoreboard(message.payload.entries);
        setBoardTotal(message.payload.total ?? 0);
        break;
      case 'placement':
        // Фаза 11 (И6прот): серверное место игрока (N>K) — приоритетнее
        // client-side computePlacement (fallback для N<=K и старого бэка).
        setPlacement(message.payload);
        break;
      case 'finished':
        setFinished(true);
        // Фаза 13 (Эпик A2): сессия завершена — токен мёртв (слоты удалены),
        // чистим, чтобы следующий заход с этим PIN не слал протухший токен.
        clearPlayerToken(pin);
        playFinish();
        break;
      case 'error':
        // Фаза 13 (Эпик B1): superseded — сокет замещён (новая вкладка слота);
        // тихое завершение: ws-слой уже закрыл соединение без реконнекта,
        // здесь только флаг для UI. Токен НЕ чистим — владелец слота не сменился.
        if (message.payload.code === ErrorCode.Superseded) {
          setSuperseded(true);
          break;
        }
        // Фаза 15 (Эпик B3): kicked — ведущий удалил из игры; код терминальный
        // для слота (как superseded): ws-слой уже закрыл сокет без реконнекта,
        // здесь — флаг конечного экрана. Токен чистим: слот заблокирован до
        // конца сессии, повторный join по нему снова получит kicked.
        if (message.payload.code === ErrorCode.Kicked) {
          setKicked(true);
          clearPlayerToken(pin);
          break;
        }
        // Фаза 13 (Эпик A2b): nick_taken — не Fatal-баннер, а экран «ник занят»
        // (сессия не началась; валидацию кандидатов делает сервер при
        // повторном join — клиент только предлагает).
        if (message.payload.code === ErrorCode.NickTaken) {
          setNickTaken(true);
          break;
        }
        // Идемпотентность: повторный ответ отбрасывается сервером — локально уже учтён.
        if (message.payload.code !== ErrorCode.AlreadyAnswered) {
          // И5: nick_locked/duplicate_nick локализуем; прочее — сырое сообщение.
          const key = wsErrorKey(message.payload.code);
          setError(key ? t(key) : message.payload.message);
        }
        break;
      default:
        break;
    }
  }

  const { status, send, sendRename } = useQuizSocket(pin, 'player', {
    joinNick: nick,
    // Фаза 13 (Эпик A2): токен читается из localStorage на момент отправки
    // каждого join (геттер, не значение) — реконнект после выдачи токена
    // (первый join → joined.playerToken → обрыв) уже несёт свежий токен.
    joinToken: () => loadPlayerToken(pin),
    onMessage: handleMessage,
  });

  function handleAnswer(answer: PlayerAnswer): void {
    if (myAnswer !== null || !question) return;
    // Жест пользователя — разблокируем AudioContext (autoplay policy) и кликаем.
    unlockAudio();
    playClick();
    setMyAnswer(answer);
    // Формируем wire-payload по типу ответа: choice/poll → option;
    // open/match/order → kind + data (протокол §8.2).
    send({ type: 'answer', payload: makeAnswerPayload(question.questionId, answer) });
  }

  /**
   * Run-вопрос (Фаза 14, эпик B): отправка позиции человечка (WS pos, §3.3).
   * RunView сам следит за частотой (~3 Гц, только при изменении и на видимой
   * вкладке) — сюда приходит уже «готовый к отправке» кадр. Классический
   * answer для run запрещён (серверный strict-зачёт по снапшоту при close).
   */
  function handlePos(x: number, y: number): void {
    if (!question) return;
    send({ type: 'pos', payload: { questionId: question.questionId, x, y } });
  }

  // Эффективная позиция: серверное значение приоритетно (N>K); fallback
  // computePlacement покрывает N<=K и старый бэк (Фаза 11, И6прот §5.2).
  const myPlacement = placement ?? computePlacement(scoreboard, currentNick);
  // Подпись «Показан топ-25 из N» — только когда список урезан (total > entries).
  const topShown =
    boardTotal > 0 && boardTotal > scoreboard.length
      ? t('player.topShown', { shown: scoreboard.length, total: boardTotal })
      : null;

  // И4 (Фаза 10): клиентский gate — при закрытии вопроса в режиме scorePause
  // игрок видит экран топа счёта (LiveLeaderboard), а не зависший вопрос.
  // Gate стоит на phase (не на reveal): работает и для poll, у которого reveal
  // не приходит. Следующий question-евент скрывает экран (case 'question'
  // сбрасывает состояние) — движение вперёд host-controlled.
  const showScorePause = joined && !finished && phase === 'question_closed' && scorePause;

  // И5: признак блокировки своего ника (хост переименовал → nickLocked).
  const selfEntry = myPlayerId !== null ? roster.find((e) => e.playerId === myPlayerId) : undefined;
  const selfLocked = selfEntry?.nickLocked ?? false;

  // Фаза 13 (Эпик B1): superseded — тихое завершение UI-сессии. Вместо игры —
  // короткое объяснение и выход; никаких звуков/баннеров-паники. Токен в
  // localStorage сохранён (владелец слота не изменился), реконнекта нет.
  // Фаза 15 (Эпик B3): конечный экран «Ведущий удалил вас из игры» — без
  // кнопок переподключения/переигровки; только выход на главную. Никаких
  // звуков/паники: факт кика приватен (другим игрокам не виден).
  if (kicked) {
    return (
      <main className="card card--wide" data-testid="player-kicked">
        <header className="screen-head">
          <h2 className="block-title">{t('player.screenTitle', { nick: currentNick })}</h2>
          <div className="screen-head-meta">
            <button type="button" className="btn btn--ghost" onClick={onExit}>
              {t('common.exit')}
            </button>
          </div>
        </header>
        <p className="muted" role="status">
          {t('join.errors.kicked')}
        </p>
      </main>
    );
  }

  if (superseded) {
    return (
      <main className="card card--wide" data-testid="player-superseded">
        <header className="screen-head">
          <h2 className="block-title">{t('player.screenTitle', { nick: currentNick })}</h2>
          <div className="screen-head-meta">
            <button type="button" className="btn btn--ghost" onClick={onExit}>
              {t('common.exit')}
            </button>
          </div>
        </header>
        <p className="muted" role="status">
          {t('join.superseded')}
        </p>
      </main>
    );
  }

  return (
    <main className="card card--wide">
      <header className="screen-head">
        <h2 className="block-title">{t('player.screenTitle', { nick: currentNick })}</h2>
        <div className="screen-head-meta">
          <SoundToggle />
          <StatusPill status={status} />
          <button type="button" className="btn btn--ghost" onClick={onExit}>
            {t('common.exit')}
          </button>
        </div>
      </header>

      {!joined && !nickTaken ? <p className="muted">{t('player.connecting')}</p> : null}

      {nickTaken ? (
        // Фаза 13 (Эпик A2b): «ник занят» — подсказки-кандидаты подставляются
        // в форму ввода одним тапом (авто-join НЕ делаем — шаг игрока);
        // занятость кандидата подтвердит сервер при повторном join.
        <section className="nick-taken" data-testid="nick-taken" role="alert">
          <h3 className="block-title">{t('join.errors.nickTaken')}</h3>
          <p className="muted">{t('join.errors.nickTakenHint')}</p>
          <div className="btn-row">
            {suggestNicks(nick).map((candidate) => (
              <button
                key={candidate}
                type="button"
                className="btn"
                data-testid="nick-suggestion"
                onClick={() => onNickRejected?.(candidate)}
              >
                {candidate}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn--ghost"
            data-testid="nick-taken-other"
            onClick={() => onNickRejected?.(null)}
          >
            {t('join.pickOtherNick')}
          </button>
        </section>
      ) : null}

      {joined && finished ? (
        <FinishScreen
          entries={scoreboard}
          highlightNick={nick}
          subtitle={
            myPlacement
              ? t('player.yourPlace', { rank: myPlacement.rank, score: myPlacement.score })
              : t('finish.thanks')
          }
          actions={
            <button type="button" className="btn btn--ghost" onClick={onExit}>
              {t('player.exitGame')}
            </button>
          }
        />
      ) : null}
      {joined && finished && topShown ? <p className="muted small">{topShown}</p> : null}

      {joined && !finished && phase === 'lobby' ? (
        <>
          <p className="muted">{t('player.waiting')}</p>
          {/* И5: доска участников + смена своего ника (disabled, если nickLocked). */}
          <LobbyRoster
            entries={roster}
            myPlayerId={myPlayerId ?? undefined}
            selfLocked={selfLocked}
            onRenameSelf={sendRename}
          />
        </>
      ) : null}

      {joined && !finished && question && !showScorePause ? (
        <>
          <QuestionView
            question={question}
            variant="answer"
            onAnswer={handleAnswer}
            onPos={handlePos}
            playerNick={currentNick}
            myAnswer={myAnswer}
            reveal={reveal}
            runDead={runDead}
            frozen={myAnswer !== null || reveal !== null}
          />
          {reveal && scoreboard.length > 0 ? (
            <>
              {/* Топ-5 + подсветка себя — компактнее на экране игрока. */}
              <LiveLeaderboard
                entries={scoreboard}
                highlightNick={nick}
                limit={5}
                title={t('player.currentRating')}
              />
              {/* Фаза 11 (И6прот): игрок вне топ-5 видит своё место (Kahoot-паттерн). */}
              {myPlacement && myPlacement.rank > 5 ? (
                <p className="muted small" data-testid="my-placement">
                  {t('player.yourPlace', { rank: myPlacement.rank, score: myPlacement.score })}
                </p>
              ) : null}
              {topShown ? <p className="muted small">{topShown}</p> : null}
            </>
          ) : null}
        </>
      ) : null}

      {showScorePause ? (
        // Экран топа счёта после закрытия вопроса (И4): топ-5 с подсветкой
        // себя + ожидание ведущего. role="status" озвучивает переход
        // screen-reader'у (aria-live polite — неагрессивно).
        <section className="score-pause" data-testid="score-pause">
          <h3 className="block-title" role="status" aria-live="polite">
            {t('player.scorePauseTitle')}
          </h3>
          <LiveLeaderboard entries={scoreboard} highlightNick={currentNick} limit={5} />
          {myPlacement && myPlacement.rank > 5 ? (
            <p className="muted small" data-testid="my-placement">
              {t('player.yourPlace', { rank: myPlacement.rank, score: myPlacement.score })}
            </p>
          ) : null}
          {topShown ? <p className="muted small">{topShown}</p> : null}
          <p className="muted small">{t('player.scorePauseWaiting')}</p>
        </section>
      ) : null}

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </main>
  );
}
